import { z } from 'zod';
import { addressTokenMatches, splitSearchTokens } from './address-text.js';
import { InspectorIdSchema } from './field.js';
import { DashboardJobStatusSchema, JobStatusSchema, InspectionJobSchema, } from './jobs.js';
import { comboPurchaseOrderNumber } from './visits.js';
export const JobOverviewTypeSchema = z.enum(['pvd', 'bty', 'both']);
export const JobOverviewFilterSchema = z.object({
    rollup: z.enum(['allocated', 'submitted', 'pending']).optional(),
    clientId: z.string().optional(),
    trancheId: z.string().optional(),
    state: z.string().optional(),
    area: z.string().optional(),
    type: JobOverviewTypeSchema.optional(),
    status: JobStatusSchema.optional(),
    dashboardStatus: DashboardJobStatusSchema.optional(),
    inspectorId: InspectorIdSchema.optional(),
    fromDate: z.string().optional(),
    toDate: z.string().optional(),
    /**
     * Free text over the accreditation code, the linked code, the ServiceM8 job
     * number, the combo purchase order, homeowner, site address and (when
     * present) phone/email. Tokens are ANDed. Applied in `matchesDimensions` so
     * the overview list, KPIs and map all see the same set.
     */
    search: z.string().optional(),
});
/**
 * Search used by Jobs (server) and Bookings (client).
 *
 * The query is split on whitespace and punctuation (`/`, `,`, `-`, …) and
 * every token must match. A token may hit any field: accreditation code,
 * linked code, ServiceM8 job number, combo purchase order (`PVD… / BTY…`),
 * homeowner, phone, email, or address. Address tokens also accept street-type
 * synonyms and a spelling within two edits once the token is at least five
 * characters (`McLaughlan` finds `Mclaughlin`). Identifiers stay exact.
 *
 * The common case is a substring hit on the haystack, which is what a
 * client-side visit list can afford on each keystroke. Edit distance runs
 * only for a token that missed that haystack.
 */
export function jobMatchesSearch(job, search) {
    const tokens = splitSearchTokens(search);
    if (tokens.length === 0)
        return true;
    const haystack = searchableText(job);
    let streetTokens;
    for (const token of tokens) {
        if (haystack.includes(token))
            continue;
        streetTokens ??= splitSearchTokens(`${job.address.line1} ${job.address.suburb}`);
        if (addressTokenMatches(token, streetTokens))
            continue;
        return false;
    }
    return true;
}
function searchableText(job) {
    const purchaseOrder = job.relatedCaseId
        ? comboPurchaseOrderNumber([job.jobId, job.relatedCaseId])
        : '';
    return [
        job.jobId,
        job.relatedCaseId ?? '',
        job.serviceM8JobNumber ?? '',
        purchaseOrder,
        job.owner.firstName,
        job.owner.lastName,
        job.address.line1,
        job.address.suburb,
        job.address.state,
        job.address.postcode,
        job.owner.phone ?? '',
        job.owner.email ?? '',
    ]
        .join('\n')
        .toLowerCase();
}
export const OverviewBreakdownSchema = z.object({
    total: z.number().int(),
    pvd: z.number().int(),
    bty: z.number().int(),
    both: z.number().int(),
});
export const ClientOverviewRowSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    allocated: OverviewBreakdownSchema,
    submitted: OverviewBreakdownSchema,
    pending: OverviewBreakdownSchema,
    /**
     * Allocated-but-not-submitted jobs whose last movement is more than 30 days
     * old. Drives the ">30 days" column — a real age count, not a status proxy.
     */
    pendingOver30: z.number().int().nonnegative(),
    status: z.enum(['on-track', 'attention-required', 'at-risk']),
    statusReason: z.string(),
});
/**
 * One geocoded job, trimmed to what the interactive map needs. Served
 * separately from the overview because the map wants every matching job, not
 * the overview's first 500, and needs none of the rest of the job record.
 */
export const JobMapPointSchema = z.object({
    jobId: z.string(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    /** Legend bucket, precomputed so the client isn't re-deriving status rules. */
    tone: z.enum(['allocated', 'scheduled', 'complete']),
    clientId: z.string(),
    suburb: z.string(),
    state: z.string(),
});
export const JobMapPointsSchema = z.object({
    points: z.array(JobMapPointSchema),
    /** Matching jobs with no coordinate yet — surfaced so the map can say so. */
    ungeocoded: z.number().int().nonnegative(),
});
export const JobsOverviewSchema = z.object({
    kpis: z.object({
        allocated: z.number().int(),
        submitted: z.number().int(),
        pendingSubmission: z.number().int(),
        atRisk: z.number().int(),
        stillToCome: z.number().int(),
        scheduled: z.number().int(),
        inspectionCompleteReportOutstanding: z.number().int(),
        oldestOutstandingDays: z.number().int().nonnegative(),
    }),
    clients: z.array(ClientOverviewRowSchema),
    milestones: z.array(z.object({
        trancheId: z.string(),
        clientId: z.string(),
        clientName: z.string(),
        trancheName: z.string(),
        allocatedTarget: z.number().int().nonnegative(),
        submitted: z.number().int().nonnegative(),
        progressPercent: z.number().nonnegative(),
        hitMilestones: z.array(z.number().int()),
        nextMilestonePercent: z.number().int().nullable(),
        nextMilestoneDate: z.string().nullable(),
        atRisk: z.boolean(),
        riskReason: z.string().nullable(),
        scheduledBeforeMilestone: z.number().int().nonnegative(),
        scheduledForecastPercent: z.number().nonnegative(),
    })),
    /**
     * The first page of matching jobs, sliced by VISIT so the two halves of a
     * combo are never split across the boundary — the grid groups them and an
     * orphaned half would render as a phantom standalone inspection.
     */
    jobs: z.array(InspectionJobSchema),
    /** Matching jobs — one per accreditation code. The billing/CER unit. */
    totalJobs: z.number().int().nonnegative(),
    /**
     * Matching visits — one per site trip, so a combo counts once. The unit the
     * grid, the map and ServiceM8 work orders all use.
     */
    totalVisits: z.number().int().nonnegative(),
    mapClusters: z.array(z.object({
        state: z.string(),
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
        approximate: z.boolean(),
        count: z.number().int().nonnegative(),
        scheduled: z.number().int().nonnegative(),
        complete: z.number().int().nonnegative(),
    })),
    analytics: z.object({
        types: OverviewBreakdownSchema,
        statuses: z.array(z.object({ status: JobStatusSchema, count: z.number().int().nonnegative() })),
        trends: z.array(z.object({
            metric: z.enum(['allocated', 'booked', 'submitted', 'completed']),
            total: z.number().int().nonnegative(),
            sufficient: z.boolean(),
            points: z.array(z.object({ date: z.string(), count: z.number().int().nonnegative() })),
        })),
    }),
    options: z.object({
        tranches: z.array(z.string()),
        states: z.array(z.string()),
        areas: z.array(z.string()),
        inspectors: z.array(z.string()),
    }),
});
//# sourceMappingURL=job-overview.js.map